import React, { useCallback } from "react";

import CardHeader from "@busbud/design-system-components/dist/CardHeader";
import Collapse from "@busbud/design-system-components/dist/Collapse";
import Divider from "@busbud/design-system-components/dist/Divider";
import CaretDownMD from "@busbud/design-system-components/dist/Icons/CaretDown/MD";

import { useStyles } from "./expandable-card.styles";
import { useStyles as useCardStyles } from "../card-with-header.styles";

export interface ExpandableCardContentProps {
  card_collapse_classes_override?: Record<string, string>;
  card_collapse_timeout_override?: React.ComponentProps<
    typeof Collapse
  >["timeout"];
  card_header_classes_override?: Record<string, string>;
  header: React.ReactNode;
  children: React.ReactNode;
  is_expanded?: boolean;
  onCollapse?: () => void;
  onExpand?: () => void;
  onClick?: () => void;
  show_divider?: boolean;
  wrapper_classes?: string;
  hidden?: boolean;
}

export const ExpandableCardContent: React.FC<ExpandableCardContentProps> = ({
  children,
  header,
  card_header_classes_override,
  is_expanded = true,
  onCollapse,
  onExpand,
  onClick,
  wrapper_classes,
  hidden = false,
  show_divider = true,
  card_collapse_classes_override,
  card_collapse_timeout_override = "auto"
}) => {
  const { cx, classes } = useStyles();
  const { classes: card_classes } = useCardStyles();

  const toggleCardContent = useCallback(() => {
    if (is_expanded && onCollapse) {
      onCollapse();
    }
    if (!is_expanded && onExpand) {
      onExpand();
    }
    if (onClick) {
      onClick();
    }
  }, [onCollapse, onClick, onExpand, is_expanded]);

  if (hidden) {
    return null;
  }

  const content = (
    <>
      <CardHeader
        title={header}
        disableTypography={typeof header !== "string"}
        className={classes.header}
        onClick={toggleCardContent}
        classes={{
          root: card_classes.header,
          ...card_header_classes_override
        }}
        data-testid="card-header"
      >
        <CaretDownMD
          color="text.quaternary.opaque"
          className={cx(classes.collapsible, {
            [classes.closed]: !is_expanded,
            [classes.opened]: is_expanded
          })}
        />
      </CardHeader>
      <Collapse
        classes={card_collapse_classes_override}
        in={is_expanded}
        timeout={card_collapse_timeout_override}
      >
        {!!show_divider && <Divider />}
        {children}
      </Collapse>
    </>
  );

  return wrapper_classes ? (
    <div className={wrapper_classes}>{content}</div>
  ) : (
    content
  );
};
